// src/components/HelpScreen.jsx
// Ajuda e Suporte — premium compacto.
// Rota: /help
//
// Mantém o layout global dash-* para não desalinha o AppSidebar.
// Estilo específico isolado em .help-*.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  ClipboardCopy,
  Headphones,
  Mail,
  Menu,
  MessageCircle,
  Search,
  X,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/help.scss";

const SUPPORT_EMAIL = "suporte@psyassistpro.com";
const SUPPORT_WHATSAPP_URL = ""; // Exemplo quando tiver número: "https://wa.me/5511999999999"

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }


  return "";
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function normalizeText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function FaqItem({ q, a, open, onToggle }) {
  return (
    <article className={`help-faq ${open ? "help-faq--open" : ""}`}>
      <button className="help-faq__head" type="button" onClick={onToggle}>
        <div className="help-faq__q">{q}</div>

        <div className={`help-faq__chev ${open ? "is-open" : ""}`} aria-hidden="true">
          <ChevronDown size={17} />
        </div>
      </button>

      {open ? (
        <div className="help-faq__body">
          <div className="help-faq__scroll">{a}</div>
        </div>
      ) : null}
    </article>
  );
}

function ContactItem({ icon: Icon, title, subtitle, onClick }) {
  return (
    <button className="help-contact" type="button" onClick={onClick}>
      <div className="help-contact__ico" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div className="help-contact__txt">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>

      <div className="help-contact__chev" aria-hidden="true">
        <ChevronRight size={16} />
      </div>
    </button>
  );
}

function Sheet({ children, onClose }) {
  return (
    <div className="help-sheet" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="help-sheet__panel" onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export default function HelpScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState("");

  const nomeUsuario = useMemo(() => readStorage(["psy_usuario"]) || "Usuário", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const faqs = useMemo(
    () => [
      {
        q: "Como posso cadastrar um novo paciente?",
        a: "Acesse o menu de Pacientes, clique em Novo Paciente e preencha os dados principais. Depois de salvar, o paciente ficará disponível para agenda, prontuário e financeiro.",
        category: "Pacientes",
      },
      {
        q: "Como cadastrar e acompanhar leads?",
        a: "Acesse a tela de Leads para registrar contatos vindos de WhatsApp, Instagram, indicação ou outros canais. Você pode marcar o lead como fechado ou não fechado e acompanhar a conversão.",
        category: "Leads",
      },
      {
        q: "Como alterar minha senha?",
        a: "Entre em Configurações, depois em Segurança. Informe a senha atual, digite a nova senha e confirme. Use uma senha forte e diferente das senhas usadas em outros sistemas.",
        category: "Segurança",
      },
      {
        q: "Onde vejo meus relatórios?",
        a: "Acesse Indicadores no menu lateral para analisar sessões, pacientes, engajamento, agenda e leads. Receitas, despesas, cobranças, análises financeiras e Receita Saúde ficam centralizadas na área Financeiro.",
        category: "Relatórios",
      },
      {
        q: "Posso acessar o sistema em mais de um dispositivo?",
        a: "Sim. Basta fazer login com sua conta. Por segurança, evite compartilhar seu acesso e altere sua senha caso suspeite de uso indevido.",
        category: "Conta",
      },
      {
        q: "Como gerenciar meu plano?",
        a: "Vá em Configurações e depois Gestão do Plano. Nessa tela você pode visualizar seu plano atual, alterar para outro plano ou solicitar cancelamento.",
        category: "Plano",
      },
    ],
    []
  );

  const filteredFaqs = useMemo(() => {
    const term = normalizeText(query);

    if (!term) return faqs;

    return faqs.filter((item) => {
      const content = normalizeText(`${item.q} ${item.a} ${item.category}`);
      return content.includes(term);
    });
  }, [faqs, query]);

  const totalFaqs = faqs.length;
  const filteredCount = filteredFaqs.length;

  useEffect(() => {
    if (!sheetOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setSheetOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sheetOpen]);

  useEffect(() => {
    setOpenIdx(filteredFaqs.length ? 0 : null);
  }, [query, filteredFaqs.length]);

  function toggleFaq(idx) {
    setOpenIdx((current) => (current === idx ? null : idx));
  }

  function openEmail() {
    const subject = encodeURIComponent("Suporte PsyAssist");
    const body = encodeURIComponent(
      `Olá, equipe PsyAssist.\n\nPreciso de ajuda com a minha conta.\n\nUsuário: ${nomeUsuario}\n\nDescrição do problema:\n`
    );

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setFeedback("E-mail de suporte copiado.");
    } catch {
      setFeedback(`E-mail de suporte: ${SUPPORT_EMAIL}`);
    }
  }

  function openWhatsApp() {
    if (!SUPPORT_WHATSAPP_URL) {
      setFeedback("WhatsApp de suporte ainda não configurado.");
      setSheetOpen(false);
      return;
    }

    window.open(SUPPORT_WHATSAPP_URL, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="dash-shell dash-shell--dark help-page">
      <div className="dash-orbs help-bg" aria-hidden="true">
        <span className="dash-orb dash-orb--a" />
        <span className="dash-orb dash-orb--b" />
        <span className="dash-orb dash-orb--c" />
        <span className="dash-orb dash-orb--d" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="dash-topbar dash-topbar--dark help-topbar">
        <button
          className="dash-btn-ghost dash-iconbtn help-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="dash-brand help-brand">
          <img src={Logo} alt="PsyAssist" className="dash-brand__logo help-brand__logo" />
          <div className="dash-brand__txt help-brand__text">
            <div className="dash-brand__title">Ajuda e Suporte</div>
            <div className="dash-brand__sub">FAQ • Atendimento • {nomeUsuario}</div>
          </div>
        </div>

        <div className="help-top-actions">
          <button className="help-topbtn help-topbtn--ghost" type="button" onClick={() => navigate("/settings")}>
            <ArrowLeft size={16} />
            Configurações
          </button>

          <button className="help-topbtn help-topbtn--primary" type="button" onClick={() => setSheetOpen(true)}>
            <Headphones size={16} />
            Contato
          </button>
        </div>
      </header>

      <main className="dash-main dash-main--dark help-main">
        <div className="help-wrap">
          <section className="help-hero">
            <div className="help-hero__main">
              <span className="help-kicker"><CircleHelp size={14} /> Central de ajuda</span>
              <h1>Suporte</h1>
              <p>
                {firstName ? `${firstName}, ` : ""}
                encontre respostas rápidas, procure por dúvidas comuns ou entre em contato com o suporte.
              </p>
            </div>

            <aside className="help-status">
              <div className="help-status__icon" aria-hidden="true">
                <Headphones size={22} />
              </div>

              <div>
                <small>Base de ajuda</small>
                <strong>{totalFaqs} perguntas</strong>
                <span>FAQ, contato e orientação rápida.</span>
              </div>
            </aside>
          </section>

          {feedback ? (
            <div className="help-feedback">
              <span>{feedback}</span>
              <button type="button" onClick={() => setFeedback("")} aria-label="Fechar aviso">
                <X size={14} />
              </button>
            </div>
          ) : null}

          <section className="help-layout">
            <article className="help-card help-card--faq">
              <div className="help-card__head">
                <div>
                  <span>Perguntas frequentes</span>
                  <h2>FAQ</h2>
                  <p>Expanda uma pergunta para ver a resposta.</p>
                </div>

                <div className="help-count">
                  {filteredCount} de {totalFaqs}
                </div>
              </div>

              <div className="help-search">
                <span aria-hidden="true"><Search size={16} /></span>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar por assunto, tela ou recurso..."
                  autoComplete="off"
                />
              </div>

              <div className="help-faqs">
                {filteredFaqs.length ? (
                  filteredFaqs.map((item, idx) => (
                    <FaqItem
                      key={`${item.category}-${item.q}`}
                      q={item.q}
                      a={item.a}
                      open={openIdx === idx}
                      onToggle={() => toggleFaq(idx)}
                    />
                  ))
                ) : (
                  <div className="help-empty">
                    Nenhuma pergunta encontrada para a busca.
                  </div>
                )}
              </div>
            </article>

            <aside className="help-card help-card--contact">
              <div className="help-card__head">
                <div>
                  <span>Atendimento</span>
                  <h2>Precisa de ajuda?</h2>
                  <p>Escolha um canal para falar com o suporte.</p>
                </div>
              </div>

              <div className="help-contact-list">
                <ContactItem
                  icon={Mail}
                  title="E-mail"
                  subtitle={SUPPORT_EMAIL}
                  onClick={openEmail}
                />

                <ContactItem
                  icon={ClipboardCopy}
                  title="Copiar e-mail"
                  subtitle="Copie o endereço para enviar depois"
                  onClick={copyEmail}
                />

                <ContactItem
                  icon={MessageCircle}
                  title="WhatsApp"
                  subtitle="Converse diretamente com o suporte"
                  onClick={openWhatsApp}
                />
              </div>

              <div className="help-note">
                Para agilizar o atendimento, descreva a tela, o erro e o que você estava tentando fazer.
              </div>

              <button className="help-btn" type="button" onClick={() => setSheetOpen(true)}>
                <span className="help-btn__ico" aria-hidden="true">
                  <Headphones size={17} />
                </span>
                Entrar em Contato
              </button>
            </aside>
          </section>
        </div>
      </main>

      {sheetOpen ? (
        <Sheet onClose={() => setSheetOpen(false)}>
          <div className="help-sheet__handle" aria-hidden="true" />

          <div className="help-sheet__title">Como deseja entrar em contato?</div>
          <div className="help-sheet__desc">Escolha um canal para falar com o suporte.</div>

          <button className="help-sheet__item" type="button" onClick={openEmail}>
            <div className="help-sheet__ico" aria-hidden="true">
              <Mail size={19} />
            </div>

            <div className="help-sheet__txt">
              <div className="help-sheet__lbl">E-mail</div>
              <div className="help-sheet__sub">{SUPPORT_EMAIL}</div>
            </div>

            <div className="help-sheet__chev" aria-hidden="true">
              <ChevronRight size={16} />
            </div>
          </button>

          <button className="help-sheet__item" type="button" onClick={copyEmail}>
            <div className="help-sheet__ico" aria-hidden="true">
              <ClipboardCopy size={19} />
            </div>

            <div className="help-sheet__txt">
              <div className="help-sheet__lbl">Copiar e-mail</div>
              <div className="help-sheet__sub">Copiar endereço para a área de transferência</div>
            </div>

            <div className="help-sheet__chev" aria-hidden="true">
              <ChevronRight size={16} />
            </div>
          </button>

          <button className="help-sheet__item" type="button" onClick={openWhatsApp}>
            <div className="help-sheet__ico" aria-hidden="true">
              <MessageCircle size={19} />
            </div>

            <div className="help-sheet__txt">
              <div className="help-sheet__lbl">WhatsApp</div>
              <div className="help-sheet__sub">Converse diretamente com o suporte</div>
            </div>

            <div className="help-sheet__chev" aria-hidden="true">
              <ChevronRight size={16} />
            </div>
          </button>

          <button className="help-sheet__close" type="button" onClick={() => setSheetOpen(false)}>
            Fechar
          </button>
        </Sheet>
      ) : null}
    </div>
  );
}
